import { DualPricingPaymentTypes } from "app/book/useBook";
import { calculateBookingTotals } from "./bookingTotals";
import { Path } from "app/path";
import {
  checkBlacklist,
  getAccount,
  selectAccount,
} from "app/redux/accountSlice";
import { getAppointmentGroupByBookingToken, rescheduleBookingByToken } from "app/redux/appointmentGroupSlice";
import { createAppointment } from "app/redux/appointmentsSlice";
import { getCart, selectCart } from "app/redux/cartSlice";
import {
  payInPersonWithoutCreditCard,
  payWithCreditCard,
  preauthWithCreditCard,
  depositWithCreditCard,
} from "app/redux/paymentTransactionSlice";
import { preserveUtcTimeToLocal } from "app/utils/formatDate";
import { setActiveShopTimeZone } from "app/utils/timezone";
import { getFormValues } from "app/utils/getFormValues";
import { useQuery } from "app/utils/useQuery";
import { addMinutes, format } from "date-fns";
import { find, includes, intersectionBy, round, set } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

// Failed requests reject with the axios response (see axiosInstance), so the
// server's reason, e.g. "Sorry, that time was just booked...", is in data.error
const bookingErrorMessage = (error: any) => error?.data?.error || error?.message || "Booking Failed";

export enum ModalTypes {
  INITIAL = "initial",
  CONFIRM_BOOKING = "confirmBooking",
}

export enum PaymentMethodTypes {
  CREDIT_CARD = "creditCard",
  PAY_IN_PERSON = "payInPerson",
  PRE_AUTH = "preAuth",
  DEPOSIT = "deposit",
  CASH = "cash",
}

export const tipOptions = [
  { label: "Tip later", value: 0 },
  { label: "18%", value: 0.18 },
  { label: "20%", value: 0.2 },
  { label: "22%", value: 0.22 },
  { label: "Other", value: 0 },
];

export const useOrder = () => {
  const { nameKey, shopKey } = useParams();
  const query = useQuery();
  const selectedDualPricingMethod = query.get("dualPricingMethod");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const account = useSelector(selectAccount);
  const cart = useSelector(selectCart);
  const shop = find(account.shops, { key: shopKey });

  useEffect(() => {
    setActiveShopTimeZone(shop?.timezone?.timeZoneId);
  }, [shop?.timezone?.timeZoneId]);

  const [appointmentStartTimes, setAppointmentStartTimes] = useState<any>([]);
  const [tipsCents, setTipsCents] = useState(0);
  const [selectedTipOption, setSelectedTipOption] = useState<any>(
    tipOptions[0]
  );
  const [showOtherTipInput, setShowOtherTipInput] = useState(false);
  const [subtotalCents, setSubtotalCents] = useState(cart.subtotalCents);
  const cashDiscountCents = cart.subtotalCents - cart.subtotalCents ;
  const [taxCents, setTaxCents] = useState(0);
  const [cardFeeCents, setCardFeeCents] = useState(0);
  const [cardFeePercentage, setCardFeePercentage] = useState(0);
  // Sent with each card payment so the sale records the dual-pricing fee,
  // the same fields the merchant portal sends.
  const feeFields = cardFeePercentage > 0
    ? { feeType: "PERCENTAGE", feeBasisPoint: Math.round(cardFeePercentage * 100) }
    : {};
  const [totalCents, setTotalCents] = useState(
    subtotalCents + tipsCents + taxCents - cashDiscountCents
  );
  const [currentModal, setCurrentModal] = useState(
    selectedDualPricingMethod === DualPricingPaymentTypes.CASH
      ? ModalTypes.CONFIRM_BOOKING
      : ModalTypes.INITIAL
  );
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodTypes | null>(
    selectedDualPricingMethod === DualPricingPaymentTypes.CASH
      ? PaymentMethodTypes.CASH
      : null
  );
  const [isCreditCardPaymentModalOpen, setIsCreditCardPaymentModalOpen] =
    useState(false);
  const [bookingPayload, setBookingPayload] = useState<any>(null);
  const [isProcessingCreditCardCharge, setIsProcessingCreditCardCharge] =
    useState(false);
  const [isPreAuthModalOpen, setIsPreAuthModalOpen] = useState(false);
  const [hasCreditCardInfoFailed, setHasCreditCardInfoFailed] = useState(false);
  const [appointmentGroupToReschedule, setAppointmentGroupToReschedule] =
    useState<any>(null);
  const [consent, setConsent] = useState<{
    isRequired: boolean;
    consent: any;
  }>({
    isRequired: false,
    consent: null,
  });
  const [hasAcceptedConsent, setHasAcceptedConsent] = useState(false);
  const groupedMerchantServices =
    Object.values(cart).length > 0
      ? Object.values(
          cart?.merchantServices.reduce((acc, item) => {
            const name = item.employee.name;
            if (!acc[name]) {
              acc[name] = { name, services: [], avatar: item.employee.avatar };
            }
            acc[name].services.push(item);
            return acc;
          }, {})
        )
      : [];

  const onEditBooking = () => {
    const timeUrl = generatePath(Path.BOOK_TIME, {
      nameKey,
      shopKey,
    } as any);

    query.delete("dualPricingMethod");
    navigate(`${timeUrl}?${query.toString()}`);
  };

  const navigateToOrderComplete = (
    choosenPaymentMethod: PaymentMethodTypes
  ) => {
    const orderCompleteUrl = generatePath(Path.ORDER_COMPLETE, {
      nameKey,
      shopKey,
    } as any);
    const cartParam = query.get("cart");

    switch (choosenPaymentMethod) {
      case PaymentMethodTypes.CREDIT_CARD:
        return navigate(
          `${orderCompleteUrl}?cart=${cartParam}&payment=${PaymentMethodTypes.CREDIT_CARD}`
        );
      case PaymentMethodTypes.PAY_IN_PERSON:
        return navigate(
          `${orderCompleteUrl}?cart=${cartParam}&payment=${PaymentMethodTypes.PAY_IN_PERSON}`
        );
      case PaymentMethodTypes.PRE_AUTH:
        return navigate(
          `${orderCompleteUrl}?cart=${cartParam}&payment=${PaymentMethodTypes.PRE_AUTH}`
        );
      case PaymentMethodTypes.DEPOSIT:
        return navigate(
          `${orderCompleteUrl}?cart=${cartParam}&payment=${PaymentMethodTypes.DEPOSIT}`
        );
      case PaymentMethodTypes.CASH:
        return navigate(
          `${orderCompleteUrl}?cart=${cartParam}&payment=${PaymentMethodTypes.CASH}`
        );
      default:
        return navigate(`${orderCompleteUrl}?cart=${cartParam}`);
    }
  };

  const makeAppointmentsWithNoPayment = async (appointmentData: any) => {
    setIsProcessingCreditCardCharge(true);
    try {
      await dispatch(
        payInPersonWithoutCreditCard({
          appointmentData,
        }) as any
      ).unwrap();
      await navigateToOrderComplete(PaymentMethodTypes.PAY_IN_PERSON);
      setIsProcessingCreditCardCharge(false);
    } catch (error) {
      setIsProcessingCreditCardCharge(false);
      // e.g. the time was taken meanwhile - say so instead of doing nothing
      toast.error(bookingErrorMessage(error));
      console.error(error);
    }
  };

  const onBook = async (event: any,cardData:any) => {
    event.preventDefault();

    console.log('event in book',event)
    if (!paymentMethod) {
      return toast.error("Please select a payment method");
    }
    if (consent?.isRequired && !hasAcceptedConsent) {
      return toast.error(
        "Please read and accept the consent form to continue."
      );
    }
    const { firstName, lastName, phone, email } = getFormValues(event.target);

    console.log('firstName',firstName)
    console.log('lastName',lastName)
    console.log('phone',phone)
    console.log('email',email)

    // try {
    //   const isBlacklisted = await dispatch(
    //     checkBlacklist({
    //       identifier: email,
    //       identifierType: "email",
    //       accountId: account.id,
    //     }) as any
    //   ).unwrap();
    //   if (isBlacklisted) {
    //     return toast.error(
    //       "Please contact this store to book your appointment."
    //     );
    //   }
    // } catch (error) {
    //   console.error(error);
    // }

    setBookingPayload({ firstName, lastName, phone, email });



    if (paymentMethod === PaymentMethodTypes.CREDIT_CARD) {
      // onPayWithCreditCard()
      // setIsCreditCardPaymentModalOpen(true);
      // return;
      try {
        await onPayWithCreditCard(cardData, { firstName, lastName, phone, email })


      } catch (error:any) {
        return toast.error(error?.message ?? "Something went wrong");
      }

    } else

    if (paymentMethod === PaymentMethodTypes.PRE_AUTH){
      // setIsPreAuthModalOpen(true);
      // return;

      try {
        await onPreAuthCreditCard(cardData, { firstName, lastName, phone, email })
      } catch (error:any) {
        return toast.error(error?.message ?? "Something went wrong");
      }
    } else

    if (paymentMethod === PaymentMethodTypes.DEPOSIT){
      try {
        await onDepositCreditCard(cardData, { firstName, lastName, phone, email })
      } catch (error:any) {
        return toast.error(error?.message ?? "Something went wrong");
      }
    }
    else {


    try {
      const appointmentData = (await createAppointmentData({
        email,
        phone,
        name: `${firstName} ${lastName}`,
        payload: { firstName, lastName, phone, email },
      })) as any;

      await makeAppointmentsWithNoPayment(appointmentData);
    } catch (error:any) {
      return toast.error(error?.message ?? "Something went wrong");

      console.error(error);
    }
    }

  };

  // Reschedule from the manage page: move the same booking to the time picked,
  // keeping what was paid. No payment step, no card form.
  const [isRescheduling, setIsRescheduling] = useState(false);
  const onConfirmReschedule = async () => {
    const bookingToken = query.get("appointmentGroupToReschedule");
    if (!bookingToken) return;
    setIsRescheduling(true);
    try {
      const appointments = await createAppointmentData({});
      await dispatch(
        rescheduleBookingByToken({ bookingToken, appointments }) as any
      ).unwrap();
      toast.success("Your appointment has been moved. We've emailed you the new time.");
      navigate(`/book/${nameKey}/manage-booking/${bookingToken}`);
    } catch (error: any) {
      toast.error(error?.error || "We couldn't move your appointment. Please try another time or contact the shop.");
    } finally {
      setIsRescheduling(false);
    }
  };

  const onPayWithCash = async () => {
    try {
      const appointmentData = await createAppointmentData({});
      await makeAppointmentsWithNoPayment(appointmentData);
    } catch (error) {
      console.error(error);
    }
  };

  const getInitialCreditCardInfo = async ({
    cardFormRef,
  }: {
    cardFormRef: any;
  }) => {
    try {
      setIsProcessingCreditCardCharge(true);
      const result = await cardFormRef.current.getNonceToken();
      if (!result) {
        setIsProcessingCreditCardCharge(false);
        setHasCreditCardInfoFailed(true);
        return;
      }

      return result;
    } catch (error) {
      setIsProcessingCreditCardCharge(false);
      console.error(error);
    }
  };

  const onPreAuthCreditCard = async (cardData: any, payload?: any) => {
    setIsProcessingCreditCardCharge(true);
    // console.log('card data in preauth-',cardDataRes)
    //
    // const cardData={payment_token_id:"36498723698-34234324-3434",
    //   ccExpiry:"12/29",
    //   // ccExpiry:"",
    // }
    const currentPayload = payload || bookingPayload;
    try {
      // const result = await getInitialCreditCardInfo({
      //   cardFormRef,
      // });
const preAuthPayload=
    {
      name: `${currentPayload?.firstName} ${currentPayload?.lastName}`,
      email: currentPayload?.email,
      phone:currentPayload?.phone
    }
      console.log('payload',preAuthPayload)

      const appointmentData = await createAppointmentData({ payload: currentPayload });
      await dispatch(
        preauthWithCreditCard({
          ccNumber:cardData?.ccNumber,
          paymentTokenId:cardData?.payment_token_id,
          expiryMonth: cardData?.ccExpiry,
          expiryYear: cardData?.ccExpiry,
          // source: `nonce-${result.nonce}`,
          name: `${currentPayload?.firstName} ${currentPayload?.lastName}`,
          email:currentPayload?.email,
          phone:currentPayload?.phone,
          totalCents,
          subtotalCents,
          taxCents,
          tipsCents,
          ...feeFields,
          appointmentData,
        }) as any
      ).unwrap();
      setIsProcessingCreditCardCharge(false);
      setIsPreAuthModalOpen(false);
      // const isDualPricingCash =
      //   selectedDualPricingMethod === DualPricingPaymentTypes.CASH;
      navigateToOrderComplete(
          PaymentMethodTypes.PRE_AUTH
      );
    } catch (error:any) {
      toast.error(bookingErrorMessage(error));
      setIsProcessingCreditCardCharge(false);
      setIsPreAuthModalOpen(false);
      setHasCreditCardInfoFailed(true);
      console.error(error);
    }
  };

  const onDepositCreditCard = async (cardData: any, payload?: any) => {
    setIsProcessingCreditCardCharge(true);
    const currentPayload = payload || bookingPayload;
    try {
      const appointmentData = await createAppointmentData({ payload: currentPayload });
      await dispatch(
        depositWithCreditCard({
          ccNumber: cardData?.ccNumber,
          paymentTokenId: cardData?.payment_token_id,
          expiryMonth: cardData?.ccExpiry,
          expiryYear: cardData?.ccExpiry,
          name: `${currentPayload?.firstName} ${currentPayload?.lastName}`,
          email: currentPayload?.email,
          phone: currentPayload?.phone,
          totalCents,
          subtotalCents,
          taxCents,
          tipsCents,
          ...feeFields,
          appointmentData,
        }) as any
      ).unwrap();
      setIsProcessingCreditCardCharge(false);
      navigateToOrderComplete(PaymentMethodTypes.DEPOSIT);
    } catch (error:any) {
      toast.error(bookingErrorMessage(error));
      setIsProcessingCreditCardCharge(false);
      setHasCreditCardInfoFailed(true);
      console.error(error);
    }
  };

  const onPayWithCreditCard = async (cardData: any, payload?: any) => {

    setIsProcessingCreditCardCharge(true);
    const currentPayload = payload || bookingPayload;
    try {
      // Create appointment data from the cart
      const appointmentData = await createAppointmentData({ payload: currentPayload });

      // Total cents for the payment
// ccCvv:"***",
      //     ccExpiry:"**/**",
      //     ccNumber:"4242********4242",
      //     errorField:null,
      //     message:"Success",
      //     payment_token_id:"1b2e4794-ffcc-4e07-9c05-c4488c74c92f",
      //     statusCode:"00",status:true});
      // Send the appointment_data and payment details to the backend
      await dispatch(
        payWithCreditCard({
          ccNumber:cardData?.ccNumber,
          paymentTokenId:cardData?.payment_token_id,
          // source: `nonce-${result.nonce}`, // Passing the nonce from the card form
          amount: Number((totalCents / 100).toFixed(2)), // Amount in dollars
          totalCents,
          subtotalCents,
          taxCents,
          tipsCents,
          ...feeFields,
          paidOnline: true,
          name: `${currentPayload?.firstName} ${currentPayload?.lastName}`,
          email:currentPayload?.email,
          expiryMonth: cardData?.ccExpiry,
          expiryYear: cardData?.ccExpiry,
          tip: tipsCents ? round(tipsCents / 100, 2) :0,
          tax: taxCents ? round(taxCents / 100, 2) :0,
          appointmentData, // Send the appointment data to the backend
        }) as any
      ).unwrap()

      setIsProcessingCreditCardCharge(false);
      navigateToOrderComplete(PaymentMethodTypes.CREDIT_CARD);
    } catch (error:any) {
      toast.error(bookingErrorMessage(error));
      setIsProcessingCreditCardCharge(false);
      setHasCreditCardInfoFailed(true);
      console.error(error);
    }
  };

  const createAppointmentData = async ({
                                         email,
                                         phone,
                                         name,
                                         payload,
                                       }: {
    email?: string;
    phone?: string;
    name?: string;
    payload?: any;
  }) => {
    const appointmentData = [];

    // 1️⃣ Parse UTC string into Date
    let currentDateTime = new Date(cart.startTime!); // UTC-safe

    const currentPayload = payload || bookingPayload;

    for (const service of cart.merchantServices) {
      const duration = service.duration;
      const employeeId = service.employee.id;

      // 2️⃣ Calculate end time (still a Date, still UTC-safe)
      const serviceEndTime = addMinutes(currentDateTime, duration);

      // 3️⃣ Send UTC ISO strings
      const appointment = {
        startTime: currentDateTime.toISOString(),
        endTime: serviceEndTime.toISOString(),
        shopId: cart.shop.id,
        cartUid: query.get("cart"),
        status: "pending",
        companyId: cart.shop.account.id,
        email: email || currentPayload?.email,
        phone: phone || currentPayload?.phone,
        name: name || `${currentPayload?.firstName} ${currentPayload?.lastName}`,
        employeeId,
        serviceIds: [service.id],
        appointmentGroupId: null,
      };

      appointmentData.push(appointment);

      // 4️⃣ Move cursor forward
      currentDateTime = serviceEndTime;
    }

    return appointmentData;
  };


  const handleConsentSettings = () => {
    if (!account.consentForm?.url || !account.consentEnabled) return;

    const businessServices = cart.merchantServices.map(
      (service) => service.businessService.id
    );

    const servicesThatNeedConsent = intersectionBy(
      businessServices,
      account.consentForm.serviceIds
    );

    if (servicesThatNeedConsent.length === 0) return;

    setConsent({
      isRequired: true,
      consent: account.consentForm,
    });
  };

  const onBack = () => {
    if (selectedDualPricingMethod === DualPricingPaymentTypes.CASH) {
      onEditBooking();
    } else {
      const orderUrl = generatePath(Path.ORDER, {
        nameKey,
        shopKey,
      } as any);
      navigate(`${orderUrl}?${query.toString()}`);
      setCurrentModal(ModalTypes.INITIAL);
    }
  };

  const init = async () => {
    const cartUid = query.get("cart");
    if (!cartUid) return;

    // Use the freshly fetched account, not the store's: this closure's
    // `account` can still be the previous value.
    let fetchedAccount: any = account;
    try {
      fetchedAccount = await dispatch(getAccount({ nameKey } as any) as any).unwrap();
    } catch (error) {
      console.error(error);
    }

    try {
      const fetchedCart = await dispatch(
        getCart({ uid: cartUid }) as any
      ).unwrap();
      setSubtotalCents(fetchedCart.subtotalCents);
      const totals = calculateBookingTotals({
        subtotalCents: fetchedCart.subtotalCents,
        taxPercentage: fetchedCart.shop.taxPercentage,
        account: fetchedAccount,
        payByCash: selectedDualPricingMethod === DualPricingPaymentTypes.CASH,
        tipsCents,
      });
      setTaxCents(totals.taxCents);
      setCardFeeCents(totals.cardFeeCents);
      setCardFeePercentage(totals.cardFeePercentage);
      setTotalCents(totals.totalCents);
      const startDate = fetchedCart!.startTime.split("T")[0];
      let currentTime = fetchedCart!.startTime.split("T")[1];
      const times = [];
      for (const service of fetchedCart.merchantServices) {
        const duration = service.duration;

        // Preserve the UTC time and convert to local
        const startTime = `${startDate}T${currentTime}`;

        // Format the start time to avoid leading zeros (if desired)
        times.push(format(preserveUtcTimeToLocal(startTime), "h:mma")); // 'H' for no leading zero

        // Add the service duration to get the end time
        const serviceEndTime = addMinutes(startTime, duration);
        currentTime = format(serviceEndTime, "HH:mm"); // Maintain HH:mm for internal logic
      }
      setAppointmentStartTimes(times);
    } catch (error) {
      console.error(error);
    }

    if (query.get("appointmentGroupToReschedule")) {
      try {
        const appointmentGroup = await dispatch(
          getAppointmentGroupByBookingToken({
            bookingToken: query.get("appointmentGroupToReschedule"),
          } as any) as any
        ).unwrap();

        if (appointmentGroup) {
          setAppointmentGroupToReschedule(appointmentGroup);
        } else {
          navigate("/");
        }
      } catch (error) {
        console.error(error);
        navigate("/");
      }
    }

    handleConsentSettings();
  };

  useEffect(() => {
    init();
  }, [cart.id, tipsCents]);

  return {
    account,
    cart,
    appointmentStartTimes,
    showOtherTipInput,
    tipsCents,
    setTipsCents,
    setShowOtherTipInput,
    selectedTipOption,
    setSelectedTipOption,
    subtotalCents,
    setSubtotalCents,
    currentModal,
    setCurrentModal,
    onEditBooking,
    paymentMethod,
    setPaymentMethod,
    onBook,
    isCreditCardPaymentModalOpen,
    setIsCreditCardPaymentModalOpen,
    onPayWithCreditCard,
    isProcessingCreditCardCharge,
    taxCents,
    isPreAuthModalOpen,
    setIsPreAuthModalOpen,
    setHasCreditCardInfoFailed,
    onPreAuthCreditCard,
    onDepositCreditCard,
    hasCreditCardInfoFailed,
    shop,
    onPayWithCash,
    bookingPayload,
    appointmentGroupToReschedule,
    onConfirmReschedule,
    isRescheduling,
    groupedMerchantServices,
    consent,
    setHasAcceptedConsent,
    selectedDualPricingMethod,
    cashDiscountCents,
    totalCents,
    onBack,
  };
};
